import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, Activity, Shield, TrendingUp, Globe, Flame, DollarSign, Radio, Heart, Tv, Gift, Gauge, Users, Bell, Briefcase, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const PreviewItem = ({ icon: Icon, label, color }) => (
  <div className="flex items-center gap-2.5 py-1.5">
    <div className={`w-6 h-6 rounded bg-${color}-500/10 flex items-center justify-center shrink-0`}>
      <Icon className={`h-3.5 w-3.5 text-${color}-400`} />
    </div>
    <span className="text-xs text-[#8A93A6]">{label}</span>
  </div>
);

export default function AnalyzeOverview() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.is_internal_admin;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Analyze</h1>
        <p className="text-[#8A93A6]">Read-only insights and performance metrics</p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Analytics */}
        <Link to="/app/analyze/dashboard" className="h-full">
          <Card className="glass hover:bg-white/5 transition-all cursor-pointer h-full border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <BarChart3 className="h-5 w-5 text-blue-500" />
                Analytics
              </CardTitle>
              <p className="text-xs text-[#8A93A6] mt-1">Creator and platform performance intelligence</p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="border-t border-white/5 pt-3 space-y-0.5">
                <PreviewItem icon={TrendingUp} label="Creator Intelligence" color="blue" />
                <PreviewItem icon={Globe} label="Platform Intelligence" color="cyan" />
                <PreviewItem icon={DollarSign} label="Revenue Intelligence" color="green" />
                <PreviewItem icon={Flame} label="Growth Recommendations" color="orange" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Live Analytics */}
        <Link to="/app/analyze/live" className="h-full">
          <Card className="glass hover:bg-white/5 transition-all cursor-pointer h-full border-white/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Activity className="h-5 w-5 text-green-500" />
                Live Analytics
              </CardTitle>
              <p className="text-xs text-[#8A93A6] mt-1">Real-time streaming performance and engagement</p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="border-t border-white/5 pt-3 space-y-0.5">
                <PreviewItem icon={Radio} label="Live Metrics" color="green" />
                <PreviewItem icon={Heart} label="Engagement Rate" color="pink" />
                <PreviewItem icon={Tv} label="Recent Live Streams" color="purple" />
                <PreviewItem icon={Gift} label="Gifts / Monetization" color="yellow" />
                <PreviewItem icon={Gauge} label="Platform Health" color="cyan" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* App Analytics (Admin Only) */}
        <Link to={isAdmin ? '/app/analyze/app' : '#'} className="h-full">
          <Card className={`glass transition-all h-full border-white/5 ${isAdmin ? 'hover:bg-white/5 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Shield className="h-5 w-5 text-pink-500" />
                App Analytics
                <Badge variant="outline" className="text-[10px] ml-auto border-pink-500/30 text-pink-400 uppercase tracking-wider font-semibold">Admin Only</Badge>
              </CardTitle>
              <p className="text-xs text-[#8A93A6] mt-1">
                {isAdmin ? 'System-wide aggregated intelligence' : 'Requires admin access'}
              </p>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="border-t border-white/5 pt-3 space-y-0.5">
                <PreviewItem icon={Gauge} label="System Performance" color="pink" />
                <PreviewItem icon={Users} label="User Metrics" color="indigo" />
                <PreviewItem icon={Bell} label="Recent System Events" color="amber" />
                <PreviewItem icon={Briefcase} label="App Profile Analytics" color="violet" />
                <PreviewItem icon={Eye} label="Platform-wide Insights" color="sky" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Empty state hint */}
      <div className="mt-10 text-center">
        <p className="text-xs text-[#555E6E]">No analytics data yet — connect platforms to begin tracking</p>
      </div>
    </div>
  );
}
